import "@/shared/styles/auth/right-area.scss";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChartColumnIncreasing, CircleCheck } from "lucide-react";
import { useWindowSize } from "usehooks-ts";

export default function RightArea() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const { width } = useWindowSize();
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide === 0) {
      setCurrentSlide(1);
    }
    if (isRightSwipe && currentSlide === 1) {
      setCurrentSlide(0);
    }
  };

  const isWindowWideSmall = width < 1890;
  const sliderDots = isWindowWideSmall ? [0, 1, 2] : [0, 1];
  return (
    <div className="right-area w-full h-full relative bg-repeat-y">
      <div className="carousel-container absolute right-0 top-0 w-full h-full flex flex-col items-center justify-center">
        <p className="carousel-title mb-10 text-white text-[35px] font-700 font-mont leading-[44.73px] tracking-[-0.015em] underline-offset-[from-font] decoration-skip-ink-none">
          {t("authSliderTitle")}
        </p>

        {/* Custom Carousel */}
        <div className="carousel w-full max-w-[800px] mx-auto">
          <div
            className="relative overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * 250}px)`,
              }}
            >
              {/* Card 1: Time & Cost savings */}
              <div className="w-[235px] h-[235px] bg-[#154938] shadow-lg rounded-md">
                <svg
                  width="237"
                  height="129"
                  viewBox="0 0 237 129"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_dd_31_2736)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M120.192 23.4749C121.022 23.8399 121.503 24.7408 121.354 25.656L117.814 47.4625H135.102C135.844 47.4625 136.524 47.8932 136.856 48.575C137.188 49.2568 137.116 50.0727 136.67 50.6825L113.148 82.8825C112.605 83.6247 111.639 83.8899 110.808 83.5252C109.978 83.1602 109.497 82.2594 109.646 81.3441L113.186 59.5375H95.8977C95.1553 59.5375 94.4765 59.1068 94.1445 58.425C93.8124 57.7432 93.8841 56.9273 94.3295 56.3175L117.852 24.1177C118.394 23.3754 119.361 23.1099 120.192 23.4749ZM99.8182 55.5125H115.5C116.076 55.5125 116.623 55.7728 116.996 56.2242C117.368 56.6755 117.528 57.2723 117.434 57.8558L114.835 73.8649L131.182 51.4875H115.5C114.924 51.4875 114.377 51.2272 114.004 50.7759C113.632 50.3245 113.472 49.7277 113.566 49.1442L116.165 33.1352L99.8182 55.5125Z"
                      fill="#7BD884"
                    />
                    <path
                      d="M99.8182 55.5125H115.5C116.076 55.5125 116.623 55.7728 116.996 56.2242C117.368 56.6755 117.528 57.2723 117.434 57.8558L114.835 73.8649L131.182 51.4875H115.5C114.924 51.4875 114.377 51.2272 114.004 50.7759C113.632 50.3245 113.472 49.7277 113.566 49.1442L116.165 33.1352L99.8182 55.5125Z"
                      fill="#7BD884"
                    />
                  </g>
                  <mask
                    id="mask0_31_2736"
                    style={{ maskType: "alpha" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="235"
                    height="107"
                  >
                    <rect width="235" height="107" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_31_2736)">
                    <path
                      d="M97.7558 -1.12615C97.7665 11.4978 95.2564 24.0004 90.3687 35.6679C85.4811 47.3354 78.3114 57.9394 69.2687 66.8745C60.226 75.8095 49.4874 82.9005 37.6658 87.7422C25.8442 92.5839 13.1713 95.0814 0.370856 95.0919"
                      stroke="url(#paint0_linear_31_2736)"
                      strokeOpacity="0.2"
                      strokeWidth="1.11619"
                    />
                    <path
                      d="M76.0414 3.74257C72.5064 15.0966 66.739 25.6527 59.0682 34.8083C51.3974 43.9639 41.9733 51.5397 31.3338 57.1031C20.6943 62.6665 9.04767 66.1084 -2.94111 67.2319C-14.9299 68.3555 -27.0259 67.1387 -38.5383 63.6508"
                      stroke="url(#paint1_linear_31_2736)"
                      strokeWidth="1.11619"
                    />
                    <path
                      d="M106.223 30.6508C105.913 42.5241 103.236 54.2211 98.3441 65.0741C93.452 75.9272 86.4404 85.7239 77.7096 93.9048C68.9787 102.086 58.6993 108.491 47.4583 112.753C36.2173 117.016 24.235 119.053 12.1956 118.747"
                      stroke="url(#paint2_linear_31_2736)"
                      strokeWidth="1.11619"
                    />
                    <path
                      d="M140.282 -1.12615C140.271 11.4978 142.781 24.0004 147.669 35.6679C152.557 47.3354 159.726 57.9394 168.769 66.8745C177.812 75.8095 188.55 82.9005 200.372 87.7422C212.193 92.5839 224.866 95.0814 237.667 95.0919"
                      stroke="url(#paint3_linear_31_2736)"
                      strokeOpacity="0.2"
                      strokeWidth="1.11619"
                    />
                    <path
                      d="M161.996 3.74257C165.531 15.0966 171.298 25.6527 178.969 34.8083C186.64 43.9639 196.064 51.5397 206.703 57.1031C217.343 62.6665 228.989 66.1084 240.978 67.2319C252.967 68.3555 265.063 67.1387 276.575 63.6508"
                      stroke="url(#paint4_linear_31_2736)"
                      strokeWidth="1.11619"
                    />
                    <path
                      d="M131.814 30.6508C132.124 42.5241 134.801 54.2211 139.693 65.0741C144.585 75.9272 151.597 85.7239 160.328 93.9048C169.058 102.086 179.338 108.491 190.579 112.753C201.82 117.016 213.802 119.053 225.842 118.747"
                      stroke="url(#paint5_linear_31_2736)"
                      strokeWidth="1.11619"
                    />
                  </g>
                  <path
                    d="M0.5 107.1H118.537H236.573"
                    stroke="white"
                    strokeOpacity="0.06"
                    strokeWidth="1.11619"
                  />
                  <defs>
                    <filter
                      id="filter0_dd_31_2736"
                      x="58.6763"
                      y="19"
                      width="113.647"
                      height="113.647"
                      filterUnits="userSpaceOnUse"
                      colorInterpolationFilters="sRGB"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feMorphology
                        radius="6.69712"
                        operator="erode"
                        in="SourceAlpha"
                        result="effect1_dropShadow_31_2736"
                      />
                      <feOffset dy="8.92949" />
                      <feGaussianBlur stdDeviation="5.58093" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="BackgroundImageFix"
                        result="effect1_dropShadow_31_2736"
                      />
                      <feColorMatrix
                        in="SourceAlpha"
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                        result="hardAlpha"
                      />
                      <feMorphology
                        radius="5.58093"
                        operator="erode"
                        in="SourceAlpha"
                        result="effect2_dropShadow_31_2736"
                      />
                      <feOffset dy="22.3237" />
                      <feGaussianBlur stdDeviation="13.9523" />
                      <feComposite in2="hardAlpha" operator="out" />
                      <feColorMatrix
                        type="matrix"
                        values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
                      />
                      <feBlend
                        mode="normal"
                        in2="effect1_dropShadow_31_2736"
                        result="effect2_dropShadow_31_2736"
                      />
                      <feBlend
                        mode="normal"
                        in="SourceGraphic"
                        in2="effect2_dropShadow_31_2736"
                        result="shape"
                      />
                    </filter>
                    <linearGradient
                      id="paint0_linear_31_2736"
                      x1="0.208238"
                      y1="-97.7423"
                      x2="0.366941"
                      y2="95.65"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0.24" stopColor="#154938" stopOpacity="0" />
                      <stop offset="1" stopColor="#7BD884" stopOpacity="0.7" />
                    </linearGradient>
                    <linearGradient
                      id="paint1_linear_31_2736"
                      x1="15.4684"
                      y1="-109.814"
                      x2="-37.3688"
                      y2="64.5883"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0.558911"
                        stopColor="#154938"
                        stopOpacity="0"
                      />
                      <stop offset="1" stopColor="#7BD884" stopOpacity="0.7" />
                    </linearGradient>
                    <linearGradient
                      id="paint2_linear_31_2736"
                      x1="98.7245"
                      y1="60.1585"
                      x2="12.9504"
                      y2="120.401"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#154938" stopOpacity="0" />
                      <stop offset="1" stopColor="#7BD884" />
                    </linearGradient>
                    <linearGradient
                      id="paint3_linear_31_2736"
                      x1="237.829"
                      y1="-97.7423"
                      x2="237.671"
                      y2="95.65"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0.24" stopColor="#154938" stopOpacity="0" />
                      <stop offset="1" stopColor="#7BD884" stopOpacity="0.7" />
                    </linearGradient>
                    <linearGradient
                      id="paint4_linear_31_2736"
                      x1="222.569"
                      y1="-109.814"
                      x2="275.406"
                      y2="64.5883"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop
                        offset="0.558911"
                        stopColor="#154938"
                        stopOpacity="0"
                      />
                      <stop offset="1" stopColor="#7BD884" stopOpacity="0.7" />
                    </linearGradient>
                    <linearGradient
                      id="paint5_linear_31_2736"
                      x1="139.313"
                      y1="60.1585"
                      x2="225.087"
                      y2="120.401"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#154938" stopOpacity="0" />
                      <stop offset="1" stopColor="#7BD884" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="flex flex-col justify-center items-start px-[13px] pb-[13px] mt-[-13px]">
                  <div className="justify-start text-white font-600 text-[18px] leading-[22px]">
                    {t("time-and-cost-savings")}
                  </div>
                  <div className="justify-start text-white/80 text-sm font-500 leading-[18px] mt-[6px]">
                    {t("time-and-cost-savings-description")}
                  </div>
                </div>
              </div>

              {/* Card 2: Data-Driven decisions */}
              <div className="w-[235px] h-[235px] bg-[#CFE6DD] shadow-lg rounded-md flex flex-col justify-between flex-shrink-0">
                <div className="flex flex-col justify-center items-start px-[13px] pb-[13px] pt-[16px]">
                  <div className="justify-start text-[#1F2223] font-600 text-[18px] leading-[22px]">
                    {t("data-driven-decisions")}
                  </div>
                  <div className="justify-start text-[#415762] text-sm font-500 leading-[20px] mt-[6px]">
                    {t("data-driven-decisions-description")}
                  </div>
                </div>
                <div className="p-[16px]">
                  <ChartColumnIncreasing color="#062724" />
                </div>
              </div>

              {/* Card 3: Reliable Outcomes */}
              <div className="w-[235px] h-[235px] bg-[#15803D] shadow-lg rounded-md flex flex-col justify-between p-4 flex-shrink-0">
                <div>
                  <CircleCheck color="white" width={26} height={26} />
                </div>
                <div className="flex flex-col">
                  <div className="justify-start text-white font-600 text-[18px] leading-[22px]">
                    {t("reliable-outcomes")}
                  </div>
                  <div className="justify-start text-white/80 text-sm font-500 leading-[18px] mt-[6px]">
                    {t("reliable-outcomes-description")}
                  </div>
                </div>
              </div>

              {/* Card 4: Smart Automation */}
              <div className="w-[235px] h-[235px] bg-[#083326] shadow-lg rounded-md flex flex-col justify-center items-center p-4 flex-shrink-0">
                <div>
                  <svg
                    width="103"
                    height="134"
                    viewBox="0 0 103 134"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M31.3853 31.5108H72.3194V61.8145H102.56C102.56 41.6255 102.56 20.9418 102.56 0.78257H0.908081V72.843H31.4478L31.3853 31.5108Z"
                      fill="#006039"
                    />
                    <path
                      d="M31.3522 103.386H0.84375V133.894H31.3522V103.386Z"
                      fill="#006039"
                    />
                    <path
                      d="M61.9549 72.8713H31.4464V103.38H61.9549V72.8713Z"
                      fill="#006039"
                    />
                    <path
                      d="M61.955 103.436L102.603 61.8081H61.955V103.436Z"
                      fill="#25B35A"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Custom Pagination Dots */}
          <div className="flex justify-start mt-6 space-x-2">
            {sliderDots.map((index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-[18px] h-[5.3px] rounded-full transition-colors duration-300 ${
                  index === currentSlide ? "bg-[#7BD884]" : "bg-[#FFFFFF99]"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
